import React, { useState, useEffect, useMemo } from 'react';

import {
  DeleteOutlined,
  PlusOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ExclamationCircleFilled
} from '@ant-design/icons';
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  Popconfirm,
  Tooltip,
  Typography,
  Alert
} from 'antd';
import { useTranslation } from 'react-i18next';

import Can from '@shared/permissions/Can';

const { Text, Paragraph } = Typography;
const { confirm } = Modal;

interface CidrRange {
  start: string;
  end: string;
  count: number;
  prefix: number;
  networkInt: number;
  broadcastInt: number;
}

/**
 * Parse CIDR notation and return the IP range
 * @param {string} cidr - CIDR notation (e.g., "192.168.1.0/24")
 * @returns {object|null} - { start, end, count } or null if invalid
 */
const parseCidrRange = (cidr: string | undefined | null): CidrRange | null => {
  if (!cidr || !cidr.includes('/')) return null;

  const [ip, prefixStr] = cidr.split('/');
  const prefix = parseInt(prefixStr, 10);

  // Validate prefix
  if (isNaN(prefix) || prefix < 0 || prefix > 32) return null;

  // Parse IP address
  const parts = ip.split('.');
  if (parts.length !== 4) return null;

  const octets = parts.map(p => parseInt(p, 10));
  if (octets.some(o => isNaN(o) || o < 0 || o > 255)) return null;

  // Convert IP to 32-bit integer
  const ipInt = (octets[0] << 24) + (octets[1] << 16) + (octets[2] << 8) + octets[3];

  // Calculate network mask
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;

  // Calculate network address (start) and broadcast address (end)
  const networkInt = (ipInt & mask) >>> 0;
  const broadcastInt = (networkInt | (~mask >>> 0)) >>> 0;

  // Convert back to IP strings
  const intToIp = (int: number) => [
    (int >>> 24) & 255,
    (int >>> 16) & 255,
    (int >>> 8) & 255,
    int & 255
  ].join('.');

  const count = broadcastInt - networkInt + 1;

  return {
    start: intToIp(networkInt),
    end: intToIp(broadcastInt),
    count: count,
    prefix: prefix,
    networkInt: networkInt,
    broadcastInt: broadcastInt
  };
};

/**
 * Check if an IP address is within a CIDR range or matches a single IP
 * @param {string} ipToCheck - IP address to check
 * @param {string} ipOrCidr - Single IP or CIDR notation
 * @returns {boolean}
 */
const isIpInRange = (ipToCheck: string | undefined | null, ipOrCidr: string | undefined | null): boolean => {
  if (!ipToCheck || !ipOrCidr) return false;

  // Parse the IP to check
  const checkParts = ipToCheck.split('.');
  if (checkParts.length !== 4) return false;
  const checkOctets = checkParts.map(p => parseInt(p, 10));
  if (checkOctets.some(o => isNaN(o) || o < 0 || o > 255)) return false;
  const checkInt = ((checkOctets[0] << 24) + (checkOctets[1] << 16) + (checkOctets[2] << 8) + checkOctets[3]) >>> 0;

  // Check if it's CIDR notation
  if (ipOrCidr.includes('/')) {
    const range = parseCidrRange(ipOrCidr);
    if (!range) return false;
    return checkInt >= range.networkInt && checkInt <= range.broadcastInt;
  } else {
    // Single IP comparison
    return ipToCheck === ipOrCidr;
  }
};

/**
 * Format the count number with locale-aware formatting
 */
const formatCount = (count: number): string => {
  if (count >= 1000000) {
    return `${(count / 1000000).toFixed(1)}M`;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1)}K`;
  }
  return count.toLocaleString();
};

interface IpAddressRecord {
  id: string;
  ip_address: string;
  description?: string;
  created_at: string;
  [key: string]: unknown;
}

interface ServiceResult<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
}

interface AccessibleIpAddressService {
  fetchList: () => Promise<ServiceResult<IpAddressRecord[]>>;
  create: (data: { ip_address: string; description?: string }) => Promise<ServiceResult>;
  delete: (id: string) => Promise<ServiceResult>;
  batchDelete: (ids: string[]) => Promise<ServiceResult>;
  getCurrentIp: () => Promise<ServiceResult<{ ip: string }>>;
  [key: string]: unknown;
}

interface AccessibleIpAddressesListProps {
  accessibleIpAddressService: AccessibleIpAddressService;
  role?: 'admin' | 'operator';
  currentUserIp?: string;
  hideCardHeader?: boolean;
}

/**
 * Accessible IP Addresses 列表組件
 *
 * @param {Object} accessibleIpAddressService - Accessible IP Address Service
 * @param {String} role - 角色 ('admin' 或 'operator')
 * @param {String} currentUserIp - 當前使用者的 IP 位址 (optional, from props or context)
 */
const AccessibleIpAddressesList: React.FC<AccessibleIpAddressesListProps> = ({
  accessibleIpAddressService,
  role = 'admin',
  currentUserIp: propUserIp
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [ipAddresses, setIpAddresses] = useState<IpAddressRecord[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [ipInputValue, setIpInputValue] = useState('');
  const [currentUserIp, setCurrentUserIp] = useState(propUserIp || '');
  const [form] = Form.useForm();

  // Delete confirmation modal state
  const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [pendingDeleteIps, setPendingDeleteIps] = useState<string[]>([]);

  // Calculate CIDR range when input changes
  const cidrRange = useMemo(() => {
    return parseCidrRange(ipInputValue?.trim());
  }, [ipInputValue]);

  // Check if current user's IP is in the range being added
  const isCurrentIpInRange = useMemo<boolean | null>(() => {
    if (!currentUserIp || !ipInputValue?.trim()) return null;
    return isIpInRange(currentUserIp, ipInputValue.trim());
  }, [currentUserIp, ipInputValue]);

  // Check if current user's IP is already covered by any existing whitelist entry
  const isCurrentIpCoveredByExisting = useMemo(() => {
    if (!currentUserIp) return false;
    return ipAddresses.some(record => isIpInRange(currentUserIp, record.ip_address));
  }, [currentUserIp, ipAddresses]);

  // Would creating this entry leave the user locked out?
  // Safe if (a) an existing rule already covers user IP, or (b) the new entry covers user IP.
  const wouldLockoutSelf = useMemo(() => {
    if (!currentUserIp || !ipInputValue?.trim()) return false;
    return !isCurrentIpCoveredByExisting && isCurrentIpInRange === false;
  }, [currentUserIp, ipInputValue, isCurrentIpCoveredByExisting, isCurrentIpInRange]);

  useEffect(() => {
    fetchIpAddresses();
    fetchCurrentUserIp();
  }, []);

  const fetchCurrentUserIp = async () => {
    try {
      const data = await accessibleIpAddressService.getCurrentIp();
      if (data.success && data.data?.ip) {
        setCurrentUserIp(data.data.ip);
      }
    } catch (error) {
      console.warn('Could not fetch current user IP:', error);
    }
  };

  const fetchIpAddresses = async () => {
    try {
      setLoading(true);
      const data = await accessibleIpAddressService.fetchList();

      if (data.success && data.data) {
        setIpAddresses(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch IP addresses:', error);
      message.error(t('shared.accessible_ip_addresses.error.fetch_failed') as string);
    } finally {
      setLoading(false);
    }
  };

  const doCreate = async (values: { ip_address: string; description?: string }) => {
    try {
      const data = await accessibleIpAddressService.create(values);

      if (data.success) {
        message.success(t('shared.accessible_ip_addresses.messages.create_success') as string);
        setIsModalVisible(false);
        setIpInputValue('');
        form.resetFields();
        fetchIpAddresses();
      } else {
        message.error(data.message || (t('shared.accessible_ip_addresses.error.create_failed') as string));
      }
    } catch (error) {
      console.error('Failed to create IP address:', error);
      message.error(t('shared.accessible_ip_addresses.error.create_failed') as string);
    }
  };

  const handleCreate = async (values: { ip_address?: string; description?: string }) => {
    const inputIp = values.ip_address?.trim();

    // Block when the new entry would leave user with no covering rule (neither
    // existing entries nor this new one cover their current IP).
    const willLockout = currentUserIp && inputIp
      && !isCurrentIpCoveredByExisting
      && !isIpInRange(currentUserIp, inputIp);

    if (willLockout) {
      // Show strong warning confirmation — explicit override required
      confirm({
        title: t('shared.accessible_ip_addresses.warning.not_in_range_title') as string,
        icon: <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />,
        content: (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              type="error"
              showIcon
              icon={<WarningOutlined />}
              message={t('shared.accessible_ip_addresses.warning.not_in_range_message')}
              description={
                <div style={{ marginTop: 8 }}>
                  <div>
                    <Text strong>{t('shared.accessible_ip_addresses.warning.your_ip')}: </Text>
                    <Text code>{currentUserIp}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text strong>{t('shared.accessible_ip_addresses.warning.adding_range')}: </Text>
                    <Text code>{inputIp}</Text>
                  </div>
                </div>
              }
            />
            <Text type="danger" strong>
              {t('shared.accessible_ip_addresses.warning.lockout_risk')}
            </Text>
          </Space>
        ),
        okText: t('shared.accessible_ip_addresses.warning.confirm_anyway') as string,
        okType: 'danger',
        cancelText: t('common.button.cancel') as string,
        onOk: () => doCreate(values as { ip_address: string; description?: string }),
        width: 500,
      });
      return;
    }

    doCreate(values as { ip_address: string; description?: string });
  };

  // 後端規則：whitelist 為空 = allow all，所以剩餘為空時不會 lockout。
  // Lockout 條件：剩餘清單非空，且沒有任何一條涵蓋目前 user IP。
  const checkDeleteWouldLockout = (ids: string[]): { willLockout: boolean; deletingMyIps: string[] } => {
    if (!currentUserIp) return { willLockout: false, deletingMyIps: [] };

    const deletingMyIps = ipAddresses
      .filter(ip => ids.includes(ip.id) && isIpInRange(currentUserIp, ip.ip_address))
      .map(ip => ip.ip_address);

    const remaining = ipAddresses.filter(ip => !ids.includes(ip.id));
    const willLockout = remaining.length > 0
      && !remaining.some(ip => isIpInRange(currentUserIp, ip.ip_address));

    return { willLockout, deletingMyIps };
  };

  const doDelete = async (id: string) => {
    try {
      const data = await accessibleIpAddressService.delete(id);

      if (data.success) {
        message.success(t('shared.accessible_ip_addresses.messages.delete_success') as string);
        fetchIpAddresses();
      } else {
        message.error(data.message || (t('shared.accessible_ip_addresses.error.delete_failed') as string));
      }
    } catch (error) {
      console.error('Failed to delete IP address:', error);
      message.error(t('shared.accessible_ip_addresses.error.delete_failed') as string);
    }
  };

  const doBatchDelete = async (ids: string[]) => {
    try {
      const data = await accessibleIpAddressService.batchDelete(ids);

      if (data.success) {
        message.success(t('shared.accessible_ip_addresses.messages.batch_delete_success') as string);
        setSelectedRowKeys([]);
        fetchIpAddresses();
      } else {
        message.error(data.message || (t('shared.accessible_ip_addresses.error.batch_delete_failed') as string));
      }
    } catch (error) {
      console.error('Failed to batch delete IP addresses:', error);
      message.error(t('shared.accessible_ip_addresses.error.batch_delete_failed') as string);
    }
  };

  const handleDelete = async (id: string) => {
    const { willLockout, deletingMyIps } = checkDeleteWouldLockout([id]);

    if (willLockout) {
      // Show confirmation modal requiring "confirm" input
      setPendingDeleteIds([id]);
      setPendingDeleteIps(deletingMyIps);
      setDeleteConfirmInput('');
      setDeleteConfirmVisible(true);
    } else {
      // Direct delete (剩餘清單仍涵蓋使用者，或刪完變空清單→allow all)
      doDelete(id);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('shared.accessible_ip_addresses.error.no_selection') as string);
      return;
    }

    const { willLockout, deletingMyIps } = checkDeleteWouldLockout(selectedRowKeys);

    if (willLockout) {
      setPendingDeleteIds(selectedRowKeys);
      setPendingDeleteIps(deletingMyIps);
      setDeleteConfirmInput('');
      setDeleteConfirmVisible(true);
    } else {
      doBatchDelete(selectedRowKeys);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmInput.toLowerCase() !== 'confirm') {
      message.error(t('shared.accessible_ip_addresses.warning.confirm_required') as string);
      return;
    }

    setDeleteConfirmVisible(false);

    if (pendingDeleteIds.length === 1) {
      await doDelete(pendingDeleteIds[0]);
    } else {
      await doBatchDelete(pendingDeleteIds);
    }

    setPendingDeleteIds([]);
    setPendingDeleteIps([]);
    setDeleteConfirmInput('');
  };

  const handleDeleteConfirmCancel = () => {
    setDeleteConfirmVisible(false);
    setPendingDeleteIds([]);
    setPendingDeleteIps([]);
    setDeleteConfirmInput('');
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setIpInputValue('');
    form.resetFields();
  };

  const columns = [
    {
      title: t('shared.accessible_ip_addresses.fields.ip_address'),
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (text: string) => {
        const range = parseCidrRange(text);
        return (
          <Space direction="vertical" size={0}>
            <Space>
              <GlobalOutlined />
              <Text code copyable>{text}</Text>
            </Space>
            {range && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 22 }}>
                {range.start} ~ {range.end} ({formatCount(range.count)} IPs)
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: t('shared.accessible_ip_addresses.fields.description'),
      dataIndex: 'description',
      key: 'description',
      render: (text: string | undefined) => text || '-',
    },
    {
      title: t('common.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: t('common.table.actions'),
      key: 'actions',
      width: 100,
      render: (_: unknown, record: IpAddressRecord) => (
        <Tooltip title={t('common.button.delete')}>
          <Can requiredPermission="accessible_ip_addresses:write">
            <Popconfirm
              title={t('shared.accessible_ip_addresses.confirm.delete')}
              onConfirm={() => handleDelete(record.id)}
              okText={t('common.button.confirm')}
              cancelText={t('common.button.cancel')}
            >
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Can>
        </Tooltip>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys as string[]),
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        {currentUserIp && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('shared.accessible_ip_addresses.your_ip')}: <Text code>{currentUserIp}</Text>
          </Text>
        )}
        <Space>
          {selectedRowKeys.length > 0 && (
            <Can requiredPermission="accessible_ip_addresses:write">
              <Popconfirm
                title={t('shared.accessible_ip_addresses.confirm.batch_delete', { count: selectedRowKeys.length })}
                onConfirm={handleBatchDelete}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
              >
                <Button danger icon={<DeleteOutlined />}>
                  {t('common.button.batch_delete')} ({selectedRowKeys.length})
                </Button>
              </Popconfirm>
            </Can>
          )}
          <Tooltip title={
            <div>
              <div>{t('shared.accessible_ip_addresses.hint.title')}</div>
              <div style={{ marginTop: 4, fontSize: 12, opacity: 0.85 }}>{t('shared.accessible_ip_addresses.hint.description')}</div>
            </div>
          }>
            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: 16, cursor: 'help' }} />
          </Tooltip>
          <Can requiredPermission="accessible_ip_addresses:write">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              {t('common.button.create')}
            </Button>
          </Can>
        </Space>
      </div>

        <Table
          dataSource={ipAddresses}
          columns={columns}
          rowKey="id"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: t('shared.accessible_ip_addresses.empty')
          }}
        />
      {/* Create Modal */}
      <Modal
        title={t('shared.accessible_ip_addresses.modal.create_title')}
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            label={t('shared.accessible_ip_addresses.fields.ip_address')}
            required
          >
            <Space.Compact style={{ width: '100%' }}>
              <Form.Item
                name="ip_address"
                noStyle
                rules={[
                  { required: true, message: t('shared.accessible_ip_addresses.validation.ip_required') as string }
                ]}
              >
                <Input
                  placeholder="203.0.113.50 or 203.0.113.0/24"
                  onChange={(e) => setIpInputValue(e.target.value)}
                  onBlur={(e) => setIpInputValue(e.target.value)}
                />
              </Form.Item>
              {currentUserIp && (
                <Button
                  onClick={() => {
                    form.setFieldsValue({ ip_address: currentUserIp });
                    setIpInputValue(currentUserIp);
                  }}
                >
                  {t('shared.accessible_ip_addresses.button.use_my_ip')}
                </Button>
              )}
            </Space.Compact>
          </Form.Item>

          {/* CIDR Range Preview */}
          {cidrRange && (
            <Alert
              type="info"
              icon={<InfoCircleOutlined />}
              showIcon
              style={{ marginBottom: 16, marginTop: -8 }}
              message={
                <Space direction="vertical" size={0}>
                  <Text>
                    {t('shared.accessible_ip_addresses.cidr_range.title')}
                  </Text>
                  <Text strong style={{ fontFamily: 'monospace' }}>
                    {cidrRange.start} ~ {cidrRange.end}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('shared.accessible_ip_addresses.cidr_range.total', { count: formatCount(cidrRange.count) as unknown as number })}
                  </Text>
                </Space>
              }
            />
          )}

          {/* Lockout error: neither existing rules nor new entry cover user IP */}
          {currentUserIp && ipInputValue?.trim() && wouldLockoutSelf && (
            <Alert
              type="error"
              icon={<WarningOutlined />}
              showIcon
              style={{ marginBottom: 16, marginTop: cidrRange ? 0 : -8 }}
              message={t('shared.accessible_ip_addresses.warning.lockout_block_title')}
              description={
                <Space direction="vertical" size={2}>
                  <Text>
                    {t('shared.accessible_ip_addresses.warning.lockout_block_message')}
                  </Text>
                  <Text type="secondary">
                    {t('shared.accessible_ip_addresses.warning.your_ip')}: <Text code>{currentUserIp}</Text>
                  </Text>
                </Space>
              }
            />
          )}

          {/* Success: new entry covers user IP */}
          {currentUserIp && ipInputValue?.trim() && isCurrentIpInRange === true && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16, marginTop: cidrRange ? 0 : -8 }}
              message={t('shared.accessible_ip_addresses.success.ip_included')}
            />
          )}

          {/* Success: user IP not in new entry but already covered by an existing rule */}
          {currentUserIp && ipInputValue?.trim() && isCurrentIpInRange === false && isCurrentIpCoveredByExisting && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 16, marginTop: cidrRange ? 0 : -8 }}
              message={t('shared.accessible_ip_addresses.success.covered_by_existing')}
            />
          )}

          <Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 16 }}>
            {t('shared.accessible_ip_addresses.hint.ip_format')}
          </Paragraph>

          <Form.Item
            name="description"
            label={t('shared.accessible_ip_addresses.fields.description')}
          >
            <Input.TextArea
              rows={2}
              placeholder={t('shared.accessible_ip_addresses.placeholder.description') as string}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('common.button.create')}
              </Button>
              <Button onClick={handleModalClose}>
                {t('common.button.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal - requires typing "confirm" */}
      <Modal
        title={
          <Space>
            <ExclamationCircleFilled style={{ color: '#ff4d4f' }} />
            {t('shared.accessible_ip_addresses.warning.delete_self_title')}
          </Space>
        }
        open={deleteConfirmVisible}
        onCancel={handleDeleteConfirmCancel}
        footer={[
          <Button key="cancel" onClick={handleDeleteConfirmCancel}>
            {t('common.button.cancel')}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            disabled={deleteConfirmInput.toLowerCase() !== 'confirm'}
            onClick={handleDeleteConfirm}
          >
            {t('common.button.delete')}
          </Button>
        ]}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="error"
            showIcon
            icon={<WarningOutlined />}
            message={t('shared.accessible_ip_addresses.warning.delete_self_message')}
            description={
              <div style={{ marginTop: 8 }}>
                <div>
                  <Text strong>{t('shared.accessible_ip_addresses.warning.your_ip')}: </Text>
                  <Text code>{currentUserIp}</Text>
                </div>
                <div style={{ marginTop: 4 }}>
                  <Text strong>{t('shared.accessible_ip_addresses.warning.deleting_ips')}: </Text>
                  {pendingDeleteIps.map((ip, index) => (
                    <Text key={index} code style={{ marginRight: 4 }}>{ip}</Text>
                  ))}
                </div>
              </div>
            }
          />

          <Text type="danger" strong>
            {t('shared.accessible_ip_addresses.warning.lockout_risk')}
          </Text>

          <div>
            <Text>{t('shared.accessible_ip_addresses.warning.type_confirm')}</Text>
            <Input
              style={{ marginTop: 8 }}
              placeholder="confirm"
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              onPressEnter={() => {
                if (deleteConfirmInput.toLowerCase() === 'confirm') {
                  handleDeleteConfirm();
                }
              }}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default AccessibleIpAddressesList;
